/**
 * useIdentity Hook
 *
 * Manages per-room cryptographic identity with Ed25519 keypairs.
 * Identities are stored in IndexedDB and are unique per room for unlinkability.
 */

import { useState, useEffect } from 'react'
import type { Role } from '@/lib/types'
import {
  generateIdentity,
  derivePeerId,
  base64ToUint8Array,
  encryptPrivateKey,
  loadPrivateKey,
} from '@/lib/crypto'
import { getIdentity, saveIdentity, type StoredIdentity } from '@/lib/storage'

export interface RoomIdentity {
  peerId: string
  displayName: string
  role: Role
  publicKey: Uint8Array
  privateKey: Uint8Array
  publicKeyBase64: string
}

export interface UseIdentityOptions {
  roomId: string
  displayName: string
  role: Role
}

export interface UseIdentityReturn {
  identity: RoomIdentity | null
  isLoading: boolean
  error: Error | null
}

/**
 * Get or create a unique identity for a room
 */
export function useIdentity(options: UseIdentityOptions): UseIdentityReturn {
  const { roomId, displayName, role } = options

  const [identity, setIdentity] = useState<RoomIdentity | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!roomId || !displayName) {
      setIsLoading(false)
      return
    }

    const loadOrCreateIdentity = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Check for existing identity
        const stored = await getIdentity(roomId)

        if (stored) {
          // Load existing identity (handles both encrypted and legacy plaintext format)
          const privateKey = await loadPrivateKey(stored.privateKey)
          const publicKey = base64ToUint8Array(stored.publicKey)

          // Update display name and role if changed
          if (stored.displayName !== displayName || stored.role !== role) {
            await saveIdentity({
              ...stored,
              displayName,
              role,
            })
          }

          setIdentity({
            peerId: stored.peerId,
            displayName,
            role,
            publicKey,
            privateKey,
            publicKeyBase64: stored.publicKey,
          })
        } else {
          // Generate new identity
          const { privateKey, publicKey, publicKeyBase64 } = await generateIdentity()
          const peerId = await derivePeerId(publicKey)

          // Encrypt the private key before storing
          const encryptedPrivateKey = await encryptPrivateKey(privateKey)

          const newIdentity: StoredIdentity = {
            roomId,
            peerId,
            displayName,
            publicKey: publicKeyBase64,
            privateKey: encryptedPrivateKey,
            role,
            createdAt: new Date(),
          }

          await saveIdentity(newIdentity)

          setIdentity({
            peerId,
            displayName,
            role,
            publicKey,
            privateKey,
            publicKeyBase64,
          })
        }

        setIsLoading(false)
      } catch (err) {
        console.error('[useIdentity] Failed to load/create identity:', err)
        setError(err instanceof Error ? err : new Error('Failed to load identity'))
        setIsLoading(false)
      }
    }

    loadOrCreateIdentity()
  }, [roomId, displayName, role])

  return {
    identity,
    isLoading,
    error,
  }
}

/**
 * Get identity synchronously if already loaded (for use in callbacks)
 */
export async function getOrCreateIdentity(
  roomId: string,
  displayName: string,
  role: Role
): Promise<RoomIdentity> {
  const stored = await getIdentity(roomId)

  if (stored) {
    // Load with decryption (handles both encrypted and legacy plaintext)
    return {
      peerId: stored.peerId,
      displayName: stored.displayName,
      role: stored.role,
      publicKey: base64ToUint8Array(stored.publicKey),
      privateKey: await loadPrivateKey(stored.privateKey),
      publicKeyBase64: stored.publicKey,
    }
  }

  // Generate new identity
  const { privateKey, publicKey, publicKeyBase64 } = await generateIdentity()
  const peerId = await derivePeerId(publicKey)

  // Encrypt private key before storing
  const encryptedPrivateKey = await encryptPrivateKey(privateKey)

  const newIdentity: StoredIdentity = {
    roomId,
    peerId,
    displayName,
    publicKey: publicKeyBase64,
    privateKey: encryptedPrivateKey,
    role,
    createdAt: new Date(),
  }

  await saveIdentity(newIdentity)

  return {
    peerId,
    displayName,
    role,
    publicKey,
    privateKey,
    publicKeyBase64,
  }
}
